import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Truck, 
  MapPin, 
  Clock, 
  AlertTriangle, 
  CheckCircle2,
  XCircle,
  Loader2,
  Navigation,
  Wrench,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SmartDispatchDialog({ 
  open, 
  onOpenChange, 
  jobs = [],
  selectedDate,
  onSelectTruck 
}) {
  const [loading, setLoading] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [jobLocation, setJobLocation] = useState(null);
  const [stats, setStats] = useState({ total: 0, available: 0 });

  // Fetch available trucks when job is selected
  const fetchAvailableTrucks = async (jobId) => {
    if (!jobId) return;
    
    setLoading(true);
    try {
      // First get job location
      const jobResponse = await axios.get(`${API}/dispatch/jobs/${jobId}/location`);
      const jobData = jobResponse.data;
      setJobLocation(jobData);
      
      // Then fetch available trucks
      const response = await axios.post(`${API}/dispatch/available-trucks`, {
        job_latitude: jobData.latitude,
        job_longitude: jobData.longitude,
        job_location: jobData.location,
        date: selectedDate
      });
      
      if (response.data.success) {
        setTrucks(response.data.trucks);
        setStats({
          total: response.data.total_count,
          available: response.data.available_count
        });
      }
    } catch (error) {
      console.error('Error fetching trucks:', error);
      toast.error('Failed to fetch available trucks');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all trucks without job filter
  const fetchAllTrucks = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/dispatch/available-trucks`, {
        date: selectedDate
      });
      
      if (response.data.success) {
        setTrucks(response.data.trucks);
        setStats({
          total: response.data.total_count,
          available: response.data.available_count
        });
        setJobLocation(null);
      }
    } catch (error) {
      console.error('Error fetching trucks:', error);
      toast.error('Failed to fetch available trucks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAllTrucks();
    }
  }, [open, selectedDate]);

  useEffect(() => {
    if (selectedJob && selectedJob !== 'all') {
      fetchAvailableTrucks(selectedJob);
    } else if (selectedJob === 'all') {
      fetchAllTrucks();
    }
  }, [selectedJob]);

  const getStatusBadge = (status, detail) => {
    switch (status) {
      case 'available':
        return <Badge className="bg-green-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Available</Badge>;
      case 'assigned':
        return <Badge className="bg-blue-600 text-white"><Calendar className="w-3 h-3 mr-1" />{detail || 'Assigned'}</Badge>;
      case 'repair_pending':
        return <Badge className="bg-amber-600 text-white"><Wrench className="w-3 h-3 mr-1" />Repair Pending</Badge>;
      case 'repair_urgent':
        return <Badge className="bg-red-600 text-white"><AlertTriangle className="w-3 h-3 mr-1" />Urgent Repair</Badge>;
      default:
        return <Badge className="bg-gray-600 text-white">{status}</Badge>;
    }
  };

  const handleSelectTruck = (truck) => {
    if (onSelectTruck) {
      onSelectTruck(truck);
      toast.success(`Selected Unit ${truck.unit_number}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Navigation className="h-5 w-5 text-blue-500" />
            Smart Dispatch - Find Available Trucks
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Job Selection */}
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block">Select Job (optional - for distance calculation)</label>
              <Select value={selectedJob} onValueChange={setSelectedJob}>
                <SelectTrigger>
                  <SelectValue placeholder="All trucks (no distance filter)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All trucks</SelectItem>
                  {(() => {
                    const seen = new Set();
                    return jobs
                      .filter(j => {
                        const key = j.job_number;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      })
                      .map(job => (
                        <SelectItem key={job.id || job.job_number} value={job.job_number || job.id}>
                          #{job.job_number} - {job.client_name} ({job.location || job.site_name || 'No location'})
                        </SelectItem>
                      ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline" 
              onClick={() => selectedJob ? fetchAvailableTrucks(selectedJob) : fetchAllTrucks()}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>

          {/* Job Location Display */}
          {jobLocation && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <MapPin className="h-4 w-4" />
                <span className="font-medium">Job Location:</span>
                <span>{jobLocation.location || 'Unknown'}</span>
                {jobLocation.client_name && (
                  <span className="text-muted-foreground">({jobLocation.client_name})</span>
                )}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="flex gap-4">
            <Badge variant="outline" className="text-base px-3 py-1">
              <Truck className="w-4 h-4 mr-2" />
              {stats.total} Total Trucks
            </Badge>
            <Badge className="bg-green-600 text-white text-base px-3 py-1">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {stats.available} Available
            </Badge>
          </div>

          {/* Trucks Table */}
          <div className="flex-1 overflow-auto border rounded-lg">
            <table className="w-full">
              <thead className="bg-slate-700 sticky top-0">
                <tr>
                  <th className="text-left p-3 font-medium text-white">Unit</th>
                  <th className="text-left p-3 font-medium text-white">Current Location</th>
                  {jobLocation && <th className="text-center p-3 font-medium text-white">Distance</th>}
                  {jobLocation && <th className="text-center p-3 font-medium text-white">ETA</th>}
                  <th className="text-center p-3 font-medium text-white">Status</th>
                  <th className="text-center p-3 font-medium text-white">Speed</th>
                  <th className="text-right p-3 font-medium text-white">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={jobLocation ? 7 : 5} className="text-center p-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      <p className="mt-2 text-muted-foreground">Loading trucks...</p>
                    </td>
                  </tr>
                ) : trucks.length === 0 ? (
                  <tr>
                    <td colSpan={jobLocation ? 7 : 5} className="text-center p-8 text-muted-foreground">
                      No trucks found with GPS data
                    </td>
                  </tr>
                ) : (
                  trucks.map((truck, idx) => (
                    <tr 
                      key={truck.unit_number} 
                      className={`border-t hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                        truck.status !== 'available' ? 'opacity-70' : ''
                      }`}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Truck className={`h-4 w-4 ${truck.status === 'available' ? 'text-green-500' : 'text-slate-400'}`} />
                          <span className="font-medium text-white">{truck.unit_number}</span>
                          {truck.device_name && truck.device_name !== truck.unit_number && (
                            <span className="text-xs text-slate-400">({truck.device_name})</span>
                          )}
                        </div>
                        {truck.truck_group && (
                          <div className="text-xs text-slate-300 mt-1">{truck.truck_group}</div>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-start gap-1">
                          <MapPin className="h-3 w-3 mt-1 text-slate-400 shrink-0" />
                          <span className="text-sm text-slate-200">{truck.current_location || 'Unknown'}</span>
                        </div>
                      </td>
                      {jobLocation && (
                        <td className="p-3 text-center">
                          {truck.distance_km !== null ? (
                            <span className="font-medium text-white">{truck.distance_km} km</span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                      )}
                      {jobLocation && (
                        <td className="p-3 text-center">
                          {truck.eta_hours !== null ? (
                            <div className="flex items-center justify-center gap-1 text-slate-200">
                              <Clock className="h-3 w-3" />
                              <span>{truck.eta_hours}h</span>
                            </div>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                      )}
                      <td className="p-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getStatusBadge(truck.status, truck.status_detail)}
                          {truck.status_detail && truck.status !== 'assigned' && (
                            <span className="text-xs text-slate-400 max-w-[150px] truncate">
                              {truck.status_detail}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span className={truck.speed_kph > 0 ? 'text-green-400 font-medium' : 'text-slate-400'}>
                          {truck.speed_kph || 0} km/h
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant={truck.status === 'available' ? 'default' : 'outline'}
                          onClick={() => handleSelectTruck(truck)}
                          disabled={truck.status === 'repair_urgent'}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
